import { useEffect, useState } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { IndianRupee, Loader2, Save, Plus, Trash2, ScrollText, Map, Zap } from "lucide-react";
import { toast } from "sonner";
import { PricingHealthWidget } from "./AdminPricingHealth";
import ZonePreviewSimulator from "../components/admin/ZonePreviewSimulator";
import AdminEtaConfig from "../components/admin/AdminEtaConfig";

/**
 * Unified slab pricing editor.
 *
 * Source of truth for the entire customer-facing price card, checkout, and
 * tracking page. Backend route: /api/admin/pricing-config (GET / PUT).
 *
 * Pricing formula:
 *   service_charges = round(slab_charge × zone_multiplier)
 *   gst             = round(service_charges × gst_rate)
 *   total           = service_charges + gst
 */
export default function AdminPricing() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slabs, setSlabs] = useState([]);
  const [zones, setZones] = useState({});
  const [gstRate, setGstRate] = useState(18); // percent
  const [overflow, setOverflow] = useState(30);
  const [surchargeFreeKg, setSurchargeFreeKg] = useState(2);
  const [surchargeRates, setSurchargeRates] = useState({ A: 0, B: 0, C: 0.16, D: 0.20 });
  const [surchargeEnabled, setSurchargeEnabled] = useState(false);
  const [pickupFee, setPickupFee] = useState(60);
  const [packingFeeSmall, setPackingFeeSmall] = useState(49);
  const [packingFeeMedium, setPackingFeeMedium] = useState(99);
  const [packingFeeLarge, setPackingFeeLarge] = useState(199);
  const [platformFee, setPlatformFee] = useState(30);
  const [hubFee, setHubFee] = useState(15);
  const [deliveryEnabled, setDeliveryEnabled] = useState(true);
  const [codFeePct, setCodFeePct] = useState(5);
  const [healthRefreshKey, setHealthRefreshKey] = useState(0);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pricing-config");
      setSlabs((data.weight_slabs || []).map((s) => ({ ...s })));
      setZones({ ...(data.zone_multipliers || {}) });
      setGstRate(Math.round((data.gst_rate ?? 0.18) * 100));
      setOverflow(data.heavy_overflow_per_kg ?? 30);
      setSurchargeFreeKg(data.longhaul_surcharge_free_kg ?? 2);
      setSurchargeRates({ A: 0, B: 0, C: 0.16, D: 0.20, ...(data.longhaul_surcharge_per_kgkm || {}) });
      setSurchargeEnabled(Boolean(data.longhaul_surcharge_enabled));
      setPickupFee(data.service_pickup_fee ?? 60);
      setPackingFeeSmall(data.service_packing_fee_small ?? 49);
      setPackingFeeMedium(data.service_packing_fee_medium ?? 99);
      setPackingFeeLarge(data.service_packing_fee_large ?? 199);
      setPlatformFee(data.service_platform_fee ?? 30);
      setHubFee(data.service_hub_fee ?? 15);
      setDeliveryEnabled(data.delivery_charges_enabled ?? true);
      setCodFeePct(data.cod_fee_pct ?? 5);
    } catch (err) {
      toast.error("Failed to load pricing config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const updateSlab = (idx, field, value) => {
    setSlabs((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const removeSlab = (idx) => {
    setSlabs((prev) => prev.filter((_, i) => i !== idx));
  };

  const addSlab = () => {
    setSlabs((prev) => [...prev, { label: "New slab", max_g: 1000, charge: 0 }]);
  };

  const updateZone = (zid, field, value) => {
    setZones((prev) => ({
      ...prev,
      [zid]: { ...prev[zid], [field]: value },
    }));
  };

  // Pick the next free single-letter zone id (E, F, …, Z), then fall back to
  // Z2/Z3 etc. if the admin somehow exceeded the alphabet. Defaults sit just
  // past the current largest km cap so a fresh zone slots in cleanly above
  // the existing range without overlapping.
  const addZone = () => {
    setZones((prev) => {
      const used = new Set(Object.keys(prev));
      let nextId = null;
      for (let code = 65; code <= 90; code++) {
        const c = String.fromCharCode(code);
        if (!used.has(c)) { nextId = c; break; }
      }
      if (!nextId) {
        let n = 2;
        while (used.has(`Z${n}`)) n++;
        nextId = `Z${n}`;
      }
      // Default max_km = (largest existing max_km or 0) + 500. Multiplier 1.0
      // so admin must consciously raise it before it bites pricing.
      const maxKms = Object.values(prev)
        .map((z) => (z.max_km == null ? 0 : Number(z.max_km)))
        .filter((n) => Number.isFinite(n));
      const baseKm = maxKms.length ? Math.max(...maxKms) : 0;
      return {
        ...prev,
        [nextId]: {
          label: `Zone ${nextId}`,
          max_km: baseKm + 500,
          multiplier: 1.0,
        },
      };
    });
    // Surcharge rate defaults to 0 for the new zone.
    setSurchargeRates((prev) => {
      const used = new Set(Object.keys(zones));
      let nextId = null;
      for (let code = 65; code <= 90; code++) {
        const c = String.fromCharCode(code);
        if (!used.has(c)) { nextId = c; break; }
      }
      if (!nextId) return prev;
      return { ...prev, [nextId]: 0 };
    });
    toast.success("New zone added — set max km + multiplier, then Save");
  };

  const removeZone = (zid) => {
    if (Object.keys(zones).length <= 1) {
      toast.error("Keep at least one zone");
      return;
    }
    setZones((prev) => {
      const next = { ...prev };
      delete next[zid];
      return next;
    });
    setSurchargeRates((prev) => {
      const next = { ...prev };
      delete next[zid];
      return next;
    });
  };

  const handleSave = async () => {
    // Validation
    const cleanSlabs = slabs
      .filter((s) => s.label && (s.charge !== "" && s.charge !== null))
      .map((s) => ({
        label: s.label,
        max_g: s.max_g === "" || s.max_g === null ? null : Number(s.max_g),
        charge: Number(s.charge),
      }));
    if (cleanSlabs.length === 0) {
      toast.error("Add at least one weight slab");
      return;
    }

    // Zone coverage gate — block save on hard errors (duplicate max_km,
    // multiple ∞-zones, invalid values). Warnings & info notes pass through
    // silently; admin already sees them in the inline panel.
    const zoneIssues = validateZones(zones);
    const blockingErrors = zoneIssues.filter((i) => i.severity === "error");
    if (blockingErrors.length > 0) {
      toast.error(
        `${blockingErrors.length} zone configuration error${blockingErrors.length > 1 ? "s" : ""} — fix the highlighted items above before saving.`,
        { duration: 6000 }
      );
      return;
    }

    const cleanZones = {};
    for (const [zid, z] of Object.entries(zones)) {
      cleanZones[zid] = {
        label: z.label || `Zone ${zid}`,
        max_km: z.max_km === "" || z.max_km === null ? null : Number(z.max_km),
        multiplier: Number(z.multiplier ?? 1),
      };
    }

    setSaving(true);
    try {
      await api.put("/admin/pricing-config", {
        weight_slabs: cleanSlabs,
        zone_multipliers: cleanZones,
        gst_rate: Number(gstRate) / 100,
        heavy_overflow_per_kg: Number(overflow),
        longhaul_surcharge_free_kg: Number(surchargeFreeKg) || 0,
        longhaul_surcharge_per_kgkm: Object.fromEntries(
          Object.entries(surchargeRates).map(([z, v]) => [z, Number(v) || 0])
        ),
        longhaul_surcharge_enabled: Boolean(surchargeEnabled),
        service_pickup_fee: Number(pickupFee) || 0,
        service_packing_fee_small: Number(packingFeeSmall) || 0,
        service_packing_fee_medium: Number(packingFeeMedium) || 0,
        service_packing_fee_large: Number(packingFeeLarge) || 0,
        service_platform_fee: Number(platformFee) || 0,
        service_hub_fee: Number(hubFee) || 0,
        delivery_charges_enabled: !!deliveryEnabled,
        cod_fee_pct: Number(codFeePct) || 0,
      });
      toast.success("Pricing updated — live across price card, checkout, and tracking.");
      await fetchConfig();
      setHealthRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update pricing");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" />
      </div>
    );

  return (
    <div className="space-y-6" data-testid="admin-pricing-page">
      <div className="animate-fadeInUp">
        <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">Pricing Configuration</h1>
        <p className="text-sm text-slate-500 mt-1">
          Single source of truth — these slabs drive the price card, checkout, and tracking page across the entire app.
        </p>
      </div>

      {/* Pricing Health Widget — compact (banner + quick check) */}
      <PricingHealthWidget refreshKey={healthRefreshKey} compact />

      {/* Sprint 47 — admin-editable delivery ETA per zone + speed */}
      <AdminEtaConfig />

      {/* Weight Slabs */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="slab-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center">
              <ScrollText className="h-5 w-5 text-[#FF6B00]" />
            </div>
            <div>
              <h2 className="font-['Outfit'] font-bold text-slate-900">Weight Slabs</h2>
              <p className="text-xs text-slate-500">Charges include pickup + packing + delivery margin (before GST).</p>
            </div>
          </div>
          <Button onClick={addSlab} variant="outline" className="rounded-xl h-9 gap-1.5" data-testid="add-slab-btn">
            <Plus className="h-4 w-4" /> Add slab
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-100">
                <th className="pb-2 font-semibold">Label</th>
                <th className="pb-2 font-semibold">Max weight (g)</th>
                <th className="pb-2 font-semibold">Charge (INR)</th>
                <th className="pb-2"></th>
              </tr>
            </thead>
            <tbody>
              {slabs.map((s, idx) => (
                <tr key={`slab-${idx}-${s.label}`} className="border-b border-slate-50" data-testid={`slab-row-${idx}`}>
                  <td className="py-2 pr-2">
                    <Input value={s.label} onChange={(e) => updateSlab(idx, "label", e.target.value)} className="rounded-lg h-9 text-sm" data-testid={`slab-label-${idx}`} />
                  </td>
                  <td className="py-2 pr-2">
                    <Input
                      type="number"
                      placeholder="∞ (last slab)"
                      value={s.max_g ?? ""}
                      onChange={(e) => updateSlab(idx, "max_g", e.target.value === "" ? null : Number(e.target.value))}
                      className="rounded-lg h-9 text-sm"
                      data-testid={`slab-maxg-${idx}`}
                    />
                  </td>
                  <td className="py-2 pr-2">
                    <Input type="number" value={s.charge ?? 0} onChange={(e) => updateSlab(idx, "charge", e.target.value)} className="rounded-lg h-9 text-sm" data-testid={`slab-charge-${idx}`} />
                  </td>
                  <td className="py-2 w-10">
                    <Button onClick={() => removeSlab(idx)} variant="ghost" size="sm" className="text-red-500 hover:bg-red-50 rounded-lg h-9 w-9 p-0" data-testid={`remove-slab-${idx}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs font-semibold text-slate-700">
              Heavy overflow rate (₹/kg above {(() => {
                // Read the cap from the last finite slab so the label always
                // matches the table — admin can change the max from 30 kg
                // to anything else and this stays in sync.
                const finite = slabs.filter((s) => s.max_g !== null && s.max_g !== Infinity && Number(s.max_g) > 0);
                if (finite.length === 0) return "the last slab";
                const lastKg = Math.round(Number(finite[finite.length - 1].max_g) / 1000);
                return `${lastKg} kg`;
              })()})
            </Label>
            <Input type="number" value={overflow} onChange={(e) => setOverflow(e.target.value)} className="mt-1 rounded-xl h-10" data-testid="overflow-rate-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">GST rate (%)</Label>
            <Input type="number" value={gstRate} onChange={(e) => setGstRate(e.target.value)} className="mt-1 rounded-xl h-10" data-testid="gst-rate-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-amber-700">COD Fee (%)</Label>
            <Input
              type="number"
              step="0.5"
              min="0"
              max="100"
              value={codFeePct}
              onChange={(e) => setCodFeePct(e.target.value)}
              className="mt-1 rounded-xl h-10 border-amber-200 bg-amber-50/40"
              placeholder="5"
              data-testid="cod-fee-pct-input"
            />
            <p className="text-[10px] text-amber-700 mt-1">Charged on COD orders only.</p>
          </div>
        </div>
      </div>

      {/* Service Charges (Pickup + Packing + Platform + Hub) — hub-routed orders */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="service-charges-section">
        <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
              <ScrollText className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="font-['Outfit'] font-bold text-slate-900">Service Charges</h2>
              <p className="text-xs text-slate-500">
                Single line on hub-routed customer invoices. Sum of these 4 fees = Service Charges. Editable here.
              </p>
            </div>
          </div>
          <Button
            type="button"
            onClick={() => setDeliveryEnabled((v) => !v)}
            variant={deliveryEnabled ? "default" : "outline"}
            className={`rounded-xl h-9 gap-1.5 ${deliveryEnabled ? "bg-emerald-600 hover:bg-emerald-700" : "border-slate-300 text-slate-600"}`}
            data-testid="delivery-charges-toggle"
          >
            {deliveryEnabled ? "Delivery Charges: ON" : "Delivery Charges: OFF"}
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="rounded-xl border border-slate-100 p-3">
            <Label className="text-[10px] uppercase font-semibold text-slate-400">Pickup ₹</Label>
            <Input type="number" min="0" value={pickupFee} onChange={(e) => setPickupFee(e.target.value)} className="mt-1 rounded-lg h-9 text-sm" data-testid="service-pickup-fee" />
          </div>
          <div className="rounded-xl border border-slate-100 p-3">
            <Label className="text-[10px] uppercase font-semibold text-slate-400">Platform ₹</Label>
            <Input type="number" min="0" value={platformFee} onChange={(e) => setPlatformFee(e.target.value)} className="mt-1 rounded-lg h-9 text-sm" data-testid="service-platform-fee" />
          </div>
          <div className="rounded-xl border border-slate-100 p-3">
            <Label className="text-[10px] uppercase font-semibold text-slate-400">Hub ₹</Label>
            <Input type="number" min="0" value={hubFee} onChange={(e) => setHubFee(e.target.value)} className="mt-1 rounded-lg h-9 text-sm" data-testid="service-hub-fee" />
          </div>
        </div>

        <div className="mt-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
            Packing fee — picked automatically by AI-detected (or manually selected) item size
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3">
              <Label className="text-[10px] uppercase font-semibold text-emerald-700">Small Packing ₹</Label>
              <Input type="number" min="0" value={packingFeeSmall} onChange={(e) => setPackingFeeSmall(e.target.value)} className="mt-1 rounded-lg h-9 text-sm bg-white" data-testid="service-packing-fee-small" />
              <p className="text-[10px] text-emerald-700 mt-1">Documents, books, small electronics</p>
            </div>
            <div className="rounded-xl border border-amber-100 bg-amber-50/40 p-3">
              <Label className="text-[10px] uppercase font-semibold text-amber-700">Medium Packing ₹</Label>
              <Input type="number" min="0" value={packingFeeMedium} onChange={(e) => setPackingFeeMedium(e.target.value)} className="mt-1 rounded-lg h-9 text-sm bg-white" data-testid="service-packing-fee-medium" />
              <p className="text-[10px] text-amber-700 mt-1">Laptops, clothing, gadgets</p>
            </div>
            <div className="rounded-xl border border-rose-100 bg-rose-50/40 p-3">
              <Label className="text-[10px] uppercase font-semibold text-rose-700">Large Packing ₹</Label>
              <Input type="number" min="0" value={packingFeeLarge} onChange={(e) => setPackingFeeLarge(e.target.value)} className="mt-1 rounded-lg h-9 text-sm bg-white" data-testid="service-packing-fee-large" />
              <p className="text-[10px] text-rose-700 mt-1">Appliances, furniture parts</p>
            </div>
          </div>
        </div>

        <div className="mt-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-100">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
            Customer Service Charges = Pickup + Packing(size) + Platform + Hub
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1" data-testid="service-charges-sum">
            <div className="text-[12px] flex items-center justify-between rounded bg-white px-2 py-1 border border-slate-100">
              <span className="text-slate-500">Small</span>
              <span className="font-bold text-slate-900">INR {deliveryEnabled ? (Number(pickupFee) || 0) + (Number(packingFeeSmall) || 0) + (Number(platformFee) || 0) + (Number(hubFee) || 0) : 0}</span>
            </div>
            <div className="text-[12px] flex items-center justify-between rounded bg-white px-2 py-1 border border-slate-100">
              <span className="text-slate-500">Medium</span>
              <span className="font-bold text-slate-900">INR {deliveryEnabled ? (Number(pickupFee) || 0) + (Number(packingFeeMedium) || 0) + (Number(platformFee) || 0) + (Number(hubFee) || 0) : 0}</span>
            </div>
            <div className="text-[12px] flex items-center justify-between rounded bg-white px-2 py-1 border border-slate-100">
              <span className="text-slate-500">Large</span>
              <span className="font-bold text-slate-900">INR {deliveryEnabled ? (Number(pickupFee) || 0) + (Number(packingFeeLarge) || 0) + (Number(platformFee) || 0) + (Number(hubFee) || 0) : 0}</span>
            </div>
          </div>
        </div>
        {!deliveryEnabled && (
          <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5" data-testid="delivery-disabled-note">
            Delivery Charges are OFF — customers will only see Weight + GST. Click the green button above to re-enable.
          </p>
        )}
      </div>

      {/* Zone Multipliers */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="zone-section">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
              <Map className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="font-['Outfit'] font-bold text-slate-900">Distance Zones</h2>
              <p className="text-xs text-slate-500">Slab charge × zone multiplier = service charges.</p>
            </div>
          </div>
          <Button
            type="button"
            onClick={addZone}
            size="sm"
            className="rounded-xl h-9 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="add-zone-btn"
            title="Add a new distance zone"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Add Zone</span>
          </Button>
        </div>

        {/* Live Zone Legend — updates immediately when admin edits multipliers / labels / max_km.
            Pure read-view; does NOT affect pricing logic or booking flow. */}
        <ZoneLegend zones={zones} />

        {/* Coverage validator — surfaces silent misconfig traps (duplicate
            max_km, dead zones, multiplier inversions, etc.) before they
            quietly mis-bill customers. Non-blocking; admin can still save
            after a "are you sure?" confirm if there are critical errors. */}
        <ZoneCoverageWarnings zones={zones} />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(zones).map(([zid, z]) => (
            <div key={zid} className="rounded-xl border border-slate-100 p-3 space-y-2 relative" data-testid={`zone-${zid}`}>
              <button
                type="button"
                onClick={() => removeZone(zid)}
                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-md hover:bg-red-50 text-slate-300 hover:text-red-600 flex items-center justify-center transition-colors"
                aria-label={`Remove zone ${zid}`}
                data-testid={`zone-${zid}-remove`}
                title="Remove zone"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
              <div className="flex items-center justify-between pr-7">
                <span className="font-bold text-slate-900">Zone {zid}</span>
                <span className="text-[10px] text-slate-400">×{Number(z.multiplier ?? 1).toFixed(2)}</span>
              </div>
              <div>
                <Label className="text-[10px] uppercase font-semibold text-slate-400">Label</Label>
                <Input value={z.label || ""} onChange={(e) => updateZone(zid, "label", e.target.value)} className="rounded-lg h-9 text-sm" data-testid={`zone-${zid}-label`} />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-semibold text-slate-400">Max km {z.max_km == null && "(∞)"}</Label>
                <Input
                  type="number"
                  placeholder="∞"
                  value={z.max_km ?? ""}
                  onChange={(e) => updateZone(zid, "max_km", e.target.value === "" ? null : Number(e.target.value))}
                  className="rounded-lg h-9 text-sm"
                  data-testid={`zone-${zid}-maxkm`}
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-semibold text-slate-400">Multiplier</Label>
                <Input
                  type="number"
                  step="0.05"
                  value={z.multiplier ?? 1}
                  onChange={(e) => updateZone(zid, "multiplier", e.target.value)}
                  className="rounded-lg h-9 text-sm"
                  data-testid={`zone-${zid}-multiplier`}
                />
              </div>
              <div>
                <Label className={`text-[10px] uppercase font-semibold ${surchargeEnabled ? "text-amber-600" : "text-slate-300"}`}>Long-haul ₹/kg/km</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0 = none"
                  value={surchargeRates[zid] ?? 0}
                  onChange={(e) => setSurchargeRates((prev) => ({ ...prev, [zid]: e.target.value }))}
                  disabled={!surchargeEnabled}
                  className={`rounded-lg h-9 text-sm ${!surchargeEnabled ? "bg-slate-50 text-slate-400 cursor-not-allowed" : ""}`}
                  data-testid={`zone-${zid}-surcharge-rate`}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Long-haul surcharge — master toggle (highlighted button + free-kg field) */}
        <div
          className={`mt-4 rounded-2xl border-2 p-4 transition-all ${
            surchargeEnabled
              ? "border-amber-400 bg-gradient-to-br from-amber-50 to-orange-50 shadow-md shadow-amber-100"
              : "border-dashed border-slate-200 bg-slate-50/40"
          }`}
          data-testid="longhaul-section"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                surchargeEnabled ? "bg-amber-500 text-white" : "bg-slate-200 text-slate-400"
              }`}>
                <Zap className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className={`font-bold text-sm ${surchargeEnabled ? "text-amber-900" : "text-slate-600"}`}>
                  Long-haul Surcharge
                  {surchargeEnabled ? (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-500 text-white">
                      ENABLED
                    </span>
                  ) : (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-slate-200 text-slate-500">
                      DISABLED
                    </span>
                  )}
                </p>
                <p className={`text-[11px] mt-0.5 ${surchargeEnabled ? "text-amber-700" : "text-slate-500"}`}>
                  When ON: heavy parcels above the free-kg threshold are billed
                  an extra ₹/kg/km per zone (set per-zone above).
                  <strong className="ml-1">Default: OFF.</strong>
                </p>
              </div>
            </div>
            <Switch
              checked={surchargeEnabled}
              onCheckedChange={(v) => setSurchargeEnabled(Boolean(v))}
              className="data-[state=checked]:bg-amber-500"
              data-testid="longhaul-enabled-toggle"
            />
          </div>

          <div className={`mt-3 ${!surchargeEnabled ? "opacity-50 pointer-events-none" : ""}`}>
            <Label className={`text-xs font-semibold ${surchargeEnabled ? "text-amber-800" : "text-slate-500"}`}>
              Free weight threshold (kg)
            </Label>
            <p className={`text-[11px] mb-2 ${surchargeEnabled ? "text-amber-700" : "text-slate-400"}`}>
              Surcharge applies only on weight <strong>above</strong> this threshold. Default 2 kg means light parcels pay no surcharge.
            </p>
            <Input
              type="number"
              step="0.5"
              min="0"
              value={surchargeFreeKg}
              onChange={(e) => setSurchargeFreeKg(e.target.value)}
              disabled={!surchargeEnabled}
              className="rounded-lg h-9 text-sm max-w-xs"
              data-testid="surcharge-free-kg-input"
            />
          </div>
        </div>
      </div>

      {/* Zone Preview Simulator — admin-only read-only quote. Uses the same
          /api/pricing/estimate endpoint as customers. Re-runs when admin
          saves the pricing config (healthRefreshKey bump). */}
      <ZonePreviewSimulator refreshKey={healthRefreshKey} />

      {/* Save */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-[#FF6B00] hover:bg-[#E65A00] rounded-xl h-11 px-6 gap-2" data-testid="save-pricing-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save changes
        </Button>
      </div>

      {/* Live preview */}
      <div className="bg-slate-900 text-slate-100 rounded-2xl p-5" data-testid="live-preview">
        <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Customer-facing breakdown</p>
        <div className="space-y-1 font-mono text-sm">
          <div className="flex justify-between"><span className="text-slate-300">Service Charges</span><span><IndianRupee className="inline h-3 w-3" /> X</span></div>
          <div className="flex justify-between text-slate-400"><span>GST {gstRate}%</span><span>+</span></div>
          <div className="flex justify-between font-bold text-[#FF6B00] text-base mt-1 pt-1 border-t border-slate-700"><span>Total</span><span>=</span></div>
        </div>
        <p className="text-[11px] text-slate-500 mt-3">
          Pickup, packing & delivery margin are bundled into "Service Charges" — customers don't see them separately.
        </p>
      </div>
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────
// Live Zone Legend — reads the admin's in-memory `zones` state so it
// updates immediately when multiplier / label / max_km are edited.
// Pure visual; does NOT affect pricing logic or booking flow.
// ───────────────────────────────────────────────────────────────────
const ZONE_LEGEND_PALETTE = {
  A: { dot: "bg-emerald-500", pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  B: { dot: "bg-blue-500",    pill: "bg-blue-50 text-blue-700 border-blue-200" },
  C: { dot: "bg-amber-500",   pill: "bg-amber-50 text-amber-700 border-amber-200" },
  D: { dot: "bg-red-500",     pill: "bg-red-50 text-red-700 border-red-200" },
};

// Cycled fallback palette so admin-added custom zones (E, F, G, …) get a
// distinct colour without crashing the legend.
const ZONE_LEGEND_FALLBACK_PALETTE = [
  { dot: "bg-purple-500",  pill: "bg-purple-50 text-purple-700 border-purple-200" },
  { dot: "bg-pink-500",    pill: "bg-pink-50 text-pink-700 border-pink-200" },
  { dot: "bg-cyan-500",    pill: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  { dot: "bg-indigo-500",  pill: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { dot: "bg-teal-500",    pill: "bg-teal-50 text-teal-700 border-teal-200" },
  { dot: "bg-orange-500",  pill: "bg-orange-50 text-orange-700 border-orange-200" },
];

function paletteForZone(zid, idx) {
  return ZONE_LEGEND_PALETTE[zid] || ZONE_LEGEND_FALLBACK_PALETTE[idx % ZONE_LEGEND_FALLBACK_PALETTE.length];
}

// ───────────────────────────────────────────────────────────────────
// Zone Coverage Validator — inspects the admin's zone config for the
// silent-misconfig traps that classify_zone() would otherwise hide:
//
//   • DUPLICATE max_km        → second zone is unreachable (same sort key)
//   • MISSING max_km on >1    → only the first zero/null-max becomes the
//                                catch-all; others below it never fire
//   • DECREASING multiplier   → distance grows but price shrinks (typo?)
//   • INVALID values          → multiplier ≤ 0 or max_km ≤ 0
//   • NO INFINITY ZONE        → distances beyond the largest max_km cap
//                                silently fall to the last zone (info)
//
// Pure read-only — does NOT block save, does NOT touch pricing logic.
// ───────────────────────────────────────────────────────────────────
function validateZones(zones) {
  const issues = [];
  const entries = Object.entries(zones || {});
  if (entries.length === 0) {
    return [{ severity: "error", message: "No zones configured. Add at least one zone." }];
  }

  // Sort by max_km ascending (Infinity sinks to bottom, matching backend)
  const sorted = entries
    .map(([id, z]) => ({
      id,
      label: z.label,
      max_km: z.max_km == null || z.max_km === "" ? Infinity : Number(z.max_km),
      multiplier: Number(z.multiplier ?? 1),
    }))
    .sort((a, b) => a.max_km - b.max_km);

  // 1. Invalid values
  sorted.forEach((z) => {
    if (Number.isFinite(z.max_km) && z.max_km <= 0) {
      issues.push({
        severity: "error",
        message: `Zone ${z.id}: max km must be greater than 0 (got ${z.max_km}).`,
      });
    }
    if (!(z.multiplier > 0)) {
      issues.push({
        severity: "error",
        message: `Zone ${z.id}: multiplier must be greater than 0 (got ${z.multiplier}).`,
      });
    }
  });

  // 2. Duplicate max_km — the lower-sorted zone wins; the other becomes dead.
  // Using a plain object as a dict because the lucide-react `Map` icon
  // import shadows the native `Map` constructor in this module's scope.
  const byMax = {};
  sorted.forEach((z) => {
    if (!Number.isFinite(z.max_km)) return;
    const key = String(z.max_km);
    (byMax[key] = byMax[key] || []).push(z.id);
  });
  Object.entries(byMax).forEach(([km, ids]) => {
    if (ids.length > 1) {
      issues.push({
        severity: "error",
        message: `Zones ${ids.join(", ")} share the same max km (${km}). Only one will ever match — give each a unique upper bound.`,
      });
    }
  });

  // 3. Multiple zones with no max_km (∞) — only the first sorted to the end
  // becomes the real catch-all. Flag if more than one.
  const infZones = sorted.filter((z) => !Number.isFinite(z.max_km));
  if (infZones.length > 1) {
    issues.push({
      severity: "error",
      message: `Multiple zones have no max km (${infZones.map((z) => z.id).join(", ")}). Only one can be the catch-all — set a finite max km on the rest.`,
    });
  }

  // 4. Multiplier non-monotonic (closer should not be more expensive than further)
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const cur = sorted[i];
    if (cur.multiplier < prev.multiplier - 1e-6) {
      issues.push({
        severity: "warning",
        message: `Zone ${cur.id} (×${cur.multiplier.toFixed(2)}) is cheaper than the closer Zone ${prev.id} (×${prev.multiplier.toFixed(2)}). Customers far away will pay less than nearby ones — likely a typo.`,
      });
    }
  }

  // 5. Largest zone has finite cap → distances beyond fall through to it
  // (not an error, just an info nudge so admin understands the behaviour)
  const largest = sorted[sorted.length - 1];
  if (largest && Number.isFinite(largest.max_km)) {
    issues.push({
      severity: "info",
      message: `Lanes beyond ${largest.max_km} km will fall through to Zone ${largest.id} (×${largest.multiplier.toFixed(2)}). Set its max km to blank for an explicit "no upper limit".`,
    });
  }

  return issues;
}

function ZoneCoverageWarnings({ zones }) {
  const issues = validateZones(zones);
  if (issues.length === 0) {
    return (
      <div
        className="mb-3 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5"
        data-testid="zone-coverage-healthy"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        <p className="text-[11px] font-semibold text-emerald-700">
          Zone coverage looks healthy — no overlaps, gaps, or pricing inversions.
        </p>
      </div>
    );
  }

  const errors = issues.filter((i) => i.severity === "error");
  const warnings = issues.filter((i) => i.severity === "warning");
  const infos = issues.filter((i) => i.severity === "info");

  const tone = errors.length
    ? { ring: "border-red-300 bg-red-50", title: "text-red-700", icon: "bg-red-500" }
    : warnings.length
      ? { ring: "border-amber-300 bg-amber-50", title: "text-amber-700", icon: "bg-amber-500" }
      : { ring: "border-blue-200 bg-blue-50", title: "text-blue-700", icon: "bg-blue-500" };

  const headline = errors.length
    ? `${errors.length} configuration error${errors.length > 1 ? "s" : ""} — zones won't price as you expect`
    : warnings.length
      ? `${warnings.length} pricing-inversion warning${warnings.length > 1 ? "s" : ""}`
      : "Zone coverage notes";

  return (
    <div
      className={`mb-3 rounded-xl border ${tone.ring} px-3 py-2.5`}
      data-testid="zone-coverage-warnings"
    >
      <div className="flex items-start gap-2">
        <span className={`w-2 h-2 rounded-full ${tone.icon} mt-1.5 shrink-0`} />
        <div className="flex-1 min-w-0">
          <p className={`text-[11px] font-bold uppercase tracking-wider ${tone.title} mb-1.5`}>
            {headline}
          </p>
          <ul className="space-y-1 text-[12px] text-slate-700">
            {errors.map((i, idx) => (
              <li key={`e${idx}`} data-testid="zone-coverage-error" className="flex items-start gap-1.5">
                <span className="text-red-600 font-bold shrink-0">✕</span>
                <span>{i.message}</span>
              </li>
            ))}
            {warnings.map((i, idx) => (
              <li key={`w${idx}`} data-testid="zone-coverage-warning" className="flex items-start gap-1.5">
                <span className="text-amber-600 font-bold shrink-0">⚠</span>
                <span>{i.message}</span>
              </li>
            ))}
            {infos.map((i, idx) => (
              <li key={`i${idx}`} data-testid="zone-coverage-info" className="flex items-start gap-1.5 text-slate-600">
                <span className="text-blue-500 font-bold shrink-0">ⓘ</span>
                <span>{i.message}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function ZoneLegend({ zones }) {
  const sorted = Object.entries(zones || {})
    .map(([id, z]) => ({ id, ...z, max_km: z.max_km == null ? Infinity : Number(z.max_km) }))
    .sort((a, b) => a.max_km - b.max_km);

  if (sorted.length === 0) return null;

  const rangeText = (cur, prev) => {
    const lowerKm = prev == null ? 0 : Math.floor(prev) + 1;
    if (!Number.isFinite(cur.max_km)) return `> ${prev ?? 0} km`;
    if (lowerKm <= 1) return `≤ ${cur.max_km} km`;
    return `${lowerKm} – ${cur.max_km} km`;
  };

  return (
    <div
      className="mb-4 rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50/60 via-indigo-50/40 to-white p-3"
      data-testid="zone-legend"
    >
      <div className="flex items-center justify-between mb-2.5">
        <p className="text-[11px] font-bold uppercase tracking-wider text-blue-800">
          Live Zone Legend
        </p>
        <p className="text-[10px] text-slate-500 italic">
          Updates automatically as you edit multipliers, labels, or km below
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
        {sorted.map((z, idx) => {
          const palette = paletteForZone(z.id, idx);
          const prevMax = idx > 0 ? sorted[idx - 1].max_km : null;
          return (
            <div
              key={z.id}
              className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 ${palette.pill}`}
              data-testid={`zone-legend-${z.id}`}
            >
              <span className={`w-3 h-3 rounded-full ${palette.dot} ring-2 ring-white shadow-sm shrink-0`} />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-1.5">
                  <span className="font-bold text-sm">Zone {z.id}</span>
                  <span
                    className="text-[11px] font-bold opacity-80"
                    data-testid={`zone-legend-${z.id}-multiplier`}
                  >
                    ×{Number(z.multiplier ?? 1).toFixed(2)}
                  </span>
                </div>
                <div className="text-[11px] font-medium opacity-90 truncate" data-testid={`zone-legend-${z.id}-label`}>
                  {z.label || "—"}
                </div>
                <div className="text-[10px] opacity-75" data-testid={`zone-legend-${z.id}-range`}>
                  {rangeText(z, prevMax)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
